import { useQuery } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import type { License, LicenseSummary } from '../../../types';

//  We can keep this data around for a bit longer since license data basically never changes.
const STALE_TIME = 1000 * 60 * 10; // 10 minutes

type LicenseResponse = License & {
  Summary?: LicenseSummary;
};

type LicenseIdOnly = {
  licenseId: string;
};

/**
 * Takes a license response and slims it down to the LicenseData type.
 * Will try to use the included `Summary` if available, otherwise falls back to `licenseComments`.
 */
function formatLicense({ licenseId }: LicenseResponse): LicenseIdOnly {
  return {
    licenseId,
  };
}

/**
 * Async function to fetch data about all licenses, format it, and return it.
 *
 * @param {boolean} onlyIds Whether to only fetch the IDs of the licenses.
 * @param {number} pageSize The number of licenses to fetch.
 * @returns {Promise<LicenseData>} The formatted license data.
 */
async function getLicenses(onlyIds: boolean, pageSize: number) {
  const { data } = await get<LicenseResponse[]>(
    `/api/licenses/?${onlyIds ? `onlyIds=${onlyIds}&` : ''}${pageSize ? `pageSize=${pageSize}` : ''}`,
  );
  if (!data) {
    throw new Error(`Licenses could not be fetched!`);
  }
  return data.map(summary => formatLicense(summary));
}

const getQueryKey = (onlyIds: boolean, pageSize: number): QueryKey => ['license', onlyIds, pageSize];

/**
 * Hook which will fetch data about all licenses.
 * Caches the license data for 10 minutes, since it's unlikely to ever change.
 *
 * @param {boolean} onlyIds Whether to only fetch the IDs of the licenses.
 * @param {number} pageSize The number of licenses to fetch.
 */
export default function useLicenses(onlyIds: boolean, pageSize: number) {
  return useQuery<LicenseIdOnly[]>({
    queryKey: getQueryKey(onlyIds, pageSize),
    queryFn: () => getLicenses(onlyIds, pageSize),
    staleTime: STALE_TIME,
  });
}
